import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type MailingCampaign = {
  id: string;
  slug: string;
  name: string;
  subject: string;
  body_html: string;
  sender_name: string;
  sender_email: string;
  target_labels: string[];
  template_key: string | null;
  brevo_campaign_id: number | null;
  brevo_last_stats: any | null;
  brevo_synced_at: string | null;
  last_sent_at: string | null;
  repeat_enabled: boolean;
  repeat_interval_days: number | null;
  repeat_send_time: string | null;
  next_send_at: string | null;
  first_send_at: string | null;
  created_at: string;
  updated_at: string;
};

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || `kampanja-${Date.now()}`;
}

export const listMailingCampaigns = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ campaigns: MailingCampaign[] }> => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("mailing_campaigns")
      .select("id, slug, name, subject, body_html, sender_name, sender_email, target_labels, template_key, brevo_campaign_id, brevo_last_stats, brevo_synced_at, last_sent_at, repeat_enabled, repeat_interval_days, repeat_send_time, next_send_at, first_send_at, created_at, updated_at")
      .order("created_at", { ascending: false });
    if (error) throw error;
    return { campaigns: (data ?? []) as unknown as MailingCampaign[] };
  });

export const createMailingCampaign = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ name: z.string().trim().min(1).max(160) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const slug = `${slugify(data.name)}-${Math.random().toString(36).slice(2, 6)}`;
    const { data: inserted, error } = await supabaseAdmin
      .from("mailing_campaigns")
      .insert({
        name: data.name,
        slug,
        subject: "",
        body_html: "",
        sender_name: "kupish.si",
        sender_email: "info@kupish.si",
      })
      .select("id")
      .single();
    if (error) throw error;
    return { id: inserted.id };
  });

export const updateMailingCampaign = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        name: z.string().trim().min(1).max(160),
        subject: z.string().trim().max(200),
        body_html: z.string().max(500_000),
        sender_name: z.string().trim().min(1).max(120),
        sender_email: z.string().trim().email().max(160),
        target_labels: z.array(z.string().trim().max(160)).max(100),
        repeat_enabled: z.boolean().optional(),
        repeat_interval_days: z.number().int().min(1).max(365).nullable().optional(),
        repeat_send_time: z
          .string()
          .trim()
          .regex(/^\d{1,2}:\d{2}$/, "Ura mora biti v obliki HH:MM")
          .nullable()
          .optional(),
        first_send_at: z.string().datetime({ offset: true }).nullable().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { computeNextSendAt } = await import("./schedule-time");
    const update: Record<string, unknown> = {
      name: data.name,
      subject: data.subject,
      body_html: data.body_html,
      sender_name: data.sender_name,
      sender_email: data.sender_email,
      target_labels: data.target_labels,
    };
    if (data.repeat_send_time !== undefined) {
      update.repeat_send_time = data.repeat_send_time;
    }
    if (data.first_send_at !== undefined) {
      update.first_send_at = data.first_send_at;
    }
    if (data.repeat_enabled !== undefined) {
      update.repeat_enabled = data.repeat_enabled;
      const intervalDays = data.repeat_interval_days ?? null;
      update.repeat_interval_days = intervalDays;
      if (data.repeat_enabled && intervalDays) {
        const { data: row } = await supabaseAdmin
          .from("mailing_campaigns")
          .select("last_sent_at")
          .eq("id", data.id)
          .maybeSingle();
        const lastSentAt = (row as any)?.last_sent_at as string | null | undefined;
        const firstSendAt = data.first_send_at ? new Date(data.first_send_at) : null;
        let nextIso: string;
        if (!lastSentAt && firstSendAt && firstSendAt.getTime() > Date.now()) {
          // Use user-specified first send date/time as the next send timestamp.
          nextIso = firstSendAt.toISOString();
        } else {
          const base = lastSentAt
            ? new Date(lastSentAt)
            : firstSendAt
              ? new Date(firstSendAt.getTime() - intervalDays * 86_400_000)
              : new Date(Date.now() - intervalDays * 86_400_000); // so first run = today at chosen time
          const next = computeNextSendAt({
            base,
            intervalDays,
            sendTime: data.repeat_send_time ?? null,
          });
          nextIso = next.toISOString();
        }
        update.next_send_at = nextIso;
      } else {
        update.next_send_at = null;
      }
    }
    const { error } = await supabaseAdmin
      .from("mailing_campaigns")
      .update(update as any)
      .eq("id", data.id);
    if (error) throw error;
    return { ok: true as const };
  });

export const applyCampaignTemplate = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        campaignId: z.string().uuid(),
        templateKey: z.enum(["fresh", "bestsellers", "trending", "stories"]),
        overwriteName: z.boolean().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { buildPlaceholderTemplate } = await import("./campaign-templates.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const rendered = buildPlaceholderTemplate(data.templateKey);
    const update: { subject: string; body_html: string; template_key: string; name?: string } = {
      subject: rendered.subject,
      body_html: rendered.body_html,
      template_key: data.templateKey,
    };
    if (data.overwriteName) update.name = rendered.name;
    const { error } = await supabaseAdmin
      .from("mailing_campaigns")
      .update(update)
      .eq("id", data.campaignId);
    if (error) throw error;
    return { ok: true as const, subject: rendered.subject, body_html: rendered.body_html };
  });

export const deleteMailingCampaign = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ id: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("mailing_campaigns")
      .delete()
      .eq("id", data.id);
    if (error) throw error;
    return { ok: true as const };
  });

export const sendBrevoMarketingCampaign = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ campaignId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { sendCampaignViaBrevo } = await import("./brevo-campaigns.server");
    const result = await sendCampaignViaBrevo(data.campaignId);
    return { ok: true as const, ...result };
  });

export const sendTestBrevoCampaign = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        campaignId: z.string().uuid(),
        toEmail: z.string().trim().email().max(160),
        subject: z.string().trim().min(1).max(200),
        body_html: z.string().trim().min(1).max(500_000),
        sender_name: z.string().trim().min(1).max(120),
        sender_email: z.string().trim().email().max(160),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { sendTestCampaignViaBrevo } = await import("./brevo-campaigns.server");
    const result = await sendTestCampaignViaBrevo(data.campaignId, data.toEmail, {
      subject: data.subject,
      body_html: data.body_html,
      sender_name: data.sender_name,
      sender_email: data.sender_email,
    });
    return { ok: true as const, ...result };
  });

export type BrevoCampaignStats = {
  brevoCampaignId: number;
  status: string;
  sent: number;
  delivered: number;
  opens: number;
  uniqueOpens: number;
  clicks: number;
  uniqueClicks: number;
  softBounces: number;
  hardBounces: number;
  unsubscriptions: number;
  complaints: number;
  syncedAt: string;
};

export const getBrevoCampaignStats = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ campaignId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data, context }): Promise<BrevoCampaignStats | null> => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { brevoFetch } = await import("./brevo-campaigns.server");
    const { data: c } = await supabaseAdmin
      .from("mailing_campaigns")
      .select("brevo_campaign_id")
      .eq("id", data.campaignId)
      .maybeSingle();
    const id = (c as any)?.brevo_campaign_id as number | null;
    if (!id) return null;
    const body = await brevoFetch(`/emailCampaigns/${id}`);
    const s = body?.statistics?.globalStats ?? {};
    const stats: BrevoCampaignStats = {
      brevoCampaignId: id,
      status: body?.status ?? "unknown",
      sent: Number(s.sent ?? 0),
      delivered: Number(s.delivered ?? 0),
      opens: Number(s.viewed ?? s.opens ?? 0),
      uniqueOpens: Number(s.uniqueViews ?? 0),
      clicks: Number(s.clickers ?? s.clicks ?? 0),
      uniqueClicks: Number(s.uniqueClicks ?? 0),
      softBounces: Number(s.softBounces ?? 0),
      hardBounces: Number(s.hardBounces ?? 0),
      unsubscriptions: Number(s.unsubscriptions ?? 0),
      complaints: Number(s.complaints ?? 0),
      syncedAt: new Date().toISOString(),
    };
    await supabaseAdmin
      .from("mailing_campaigns")
      .update({ brevo_last_stats: stats, brevo_synced_at: stats.syncedAt })
      .eq("id", data.campaignId);
    return stats;
  });
